/**
 * The boundary latch: hysteresis plus debounce over a signed distance.
 *
 * This tiny piece of state is what stands between a working world and an
 * unusable one. A listener standing at the edge of a zone has a position that
 * wanders by metres, so a plain "is the point inside?" test flips many times a
 * minute and re-fires the trigger on every flip. Two defences are applied
 * together: entering requires being properly inside (distance ≤ 0) while
 * leaving requires being clear of the boundary by `exitMarginM`, and any
 * change of state must hold for `debounceMs` before it counts.
 */
export interface LatchState {
  inside: boolean
  /** When the contrary reading first appeared, if it is still being debounced. */
  pendingSince: number | undefined
}

export interface LatchOptions {
  /** How far outside the boundary the listener must get before they count as out. */
  exitMarginM: number
  /** How long a change must persist to be believed. */
  debounceMs: number
}

export interface LatchResult {
  inside: boolean
  entered: boolean
  exited: boolean
}

export function createLatch(inside = false): LatchState {
  return { inside, pendingSince: undefined }
}

/**
 * Feeds one reading to the latch. `signedDistanceM` is negative inside the
 * boundary and positive outside. Mutates and returns the transition, if any.
 */
export function updateLatch(
  state: LatchState,
  signedDistanceM: number,
  options: LatchOptions,
  nowMs: number,
): LatchResult {
  // Widening the boundary once inside is the hysteresis: you must genuinely
  // walk away to be counted as having left.
  const threshold = state.inside ? options.exitMarginM : 0
  const reading = signedDistanceM <= threshold

  if (reading === state.inside) {
    state.pendingSince = undefined
    return { inside: state.inside, entered: false, exited: false }
  }

  state.pendingSince ??= nowMs

  if (nowMs - state.pendingSince >= options.debounceMs) {
    state.inside = reading
    state.pendingSince = undefined
    return { inside: reading, entered: reading, exited: !reading }
  }

  return { inside: state.inside, entered: false, exited: false }
}
